<template>
  <div
    :class="cellClass"
    :draggable="canDrag"
    @click="activate"
    @dragstart="handleDragStart"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
    @dragend="handleDragEnd"
  >
    <div v-if="dropHint" :class="dropHintClass">
      {{ dropHint }}
    </div>

    <template v-if="customer">
      <div class="flex h-full flex-col justify-start gap-1.5">
        <div v-if="isDraggingSource" class="inline-flex w-fit rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
          拖动中
        </div>
        <div class="break-words text-sm font-medium leading-5 text-slate-900">{{ companyName }}</div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-[10px] uppercase tracking-wide text-slate-400">额度</span>
          <span class="text-sm font-semibold leading-4 text-slate-900">{{ formatWanAmount(customer.targetOutputAmount) }}</span>
        </div>
        <div class="flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
          <span class="text-[10px] uppercase tracking-wide text-slate-400">利率</span>
          <span class="font-medium text-slate-700">{{ formatProfitRate(customer.targetProfitRate) }}</span>
          <span v-if="isFullProfitRateCustomer(customer)" class="status-badge bg-amber-50 text-amber-700">100%</span>
        </div>
        <div class="space-y-0.5 text-[11px] leading-4 text-slate-500">
          <div class="truncate">
            <span class="text-[10px] uppercase tracking-wide text-slate-400">进项</span>
            <span class="ml-1 text-slate-600">{{ formatProductQuotaSummary(customer.inputProductQuotas).shortText }}</span>
          </div>
          <div class="truncate">
            <span class="text-[10px] uppercase tracking-wide text-slate-400">销项</span>
            <span class="ml-1 text-slate-600">{{ formatProductQuotaSummary(customer.outputProductQuotas).shortText }}</span>
          </div>
        </div>
        <div class="flex flex-wrap gap-1 text-[10px]">
          <span
            v-if="allocationSummary.enabledCount > 0"
            class="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700"
            :title="`已指定 ${formatWanAmount(allocationSummary.totalAmount)}`"
          >
            下游 {{ allocationSummary.enabledCount }}
          </span>
          <span
            v-if="preferenceSummary.enabledCount > 0"
            class="rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700"
            :title="preferenceSummary.shortText"
          >
            偏好 {{ preferenceSummary.enabledCount }}
          </span>
        </div>
      </div>
      <div class="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          class="table-action-button is-primary"
          v-permission="'enterprise-invoice:plan-customer:manage'"
          title="编辑客户" aria-label="编辑客户"
          :disabled="syncing"
          @click.stop="emit('edit-customer', customer)"
        >
          <PencilSquareIcon class="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          class="table-action-button is-danger"
          v-permission="'enterprise-invoice:plan-customer:manage'"
          title="删除客户" aria-label="删除客户"
          :disabled="syncing"
          @click.stop="emit('delete', customer)"
        >
          <TrashIcon class="h-3.5 w-3.5" />
        </button>
      </div>
    </template>

    <template v-else>
      <button
        type="button"
        class="flex h-full min-h-[112px] w-full flex-col items-center justify-center gap-1 border border-dashed border-slate-200 text-slate-400 transition-colors hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-500"
        v-permission="'enterprise-invoice:plan-customer:manage'"
        :disabled="syncing"
        @click.stop="emit('create-customer', { region })"
      >
        <PlusIcon class="h-4 w-4" />
        <span class="text-[11px] font-medium">新增客户</span>
      </button>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import {
  displayInvoiceCompanyName,
  formatProfitRate,
  formatWanAmount,
  isFullProfitRateCustomer
} from '../utils'
import {
  downstreamAllocationSummary,
  downstreamPreferenceSummary
} from '../downstreamRules.mjs'
import {
  formatProductQuotaSummary
} from '../productQuota'

const props = defineProps({
  region: { type: Object, required: true },
  regions: { type: Array, default: () => [] },
  customer: { type: Object, default: null },
  rowIndex: { type: Number, default: null },
  draggingCustomerId: { type: [Number, String], default: null },
  downstreamAllocations: { type: Array, default: () => [] },
  downstreamTagPreferences: { type: Array, default: () => [] },
  syncing: { type: Boolean, default: false }
})

const emit = defineEmits([
  'create-customer',
  'edit-customer',
  'delete',
  'drag-start',
  'drag-over',
  'drag-leave',
  'drop-customer',
  'drag-end'
])

const dropPosition = ref(null)

const companyName = computed(() => displayInvoiceCompanyName(props.customer))
const isHundredPercentCustomer = computed(() => isFullProfitRateCustomer(props.customer))
const canDrag = computed(() => Boolean(props.customer?.id) && !props.syncing)
const isDraggingSource = computed(() => props.customer?.id != null && String(props.customer.id) === String(props.draggingCustomerId))
const isDropTarget = computed(() => Boolean(dropPosition.value) && !isDraggingSource.value)
const allocationSummary = computed(() => downstreamAllocationSummary(props.downstreamAllocations, props.customer))
const preferenceSummary = computed(() => downstreamPreferenceSummary(props.downstreamTagPreferences, props.customer?.id))
const dropHint = computed(() => {
  if (!dropPosition.value || isDraggingSource.value) return ''
  if (dropPosition.value === 'before') return '插入到此客户前'
  if (dropPosition.value === 'after') return '插入到此客户后'
  return '拖放到此区域'
})
const dropHintClass = computed(() => [
  'pointer-events-none absolute left-2 right-2 z-20 rounded-md border px-2 py-1 text-center text-[11px] font-medium shadow-sm',
  dropPosition.value === 'after' ? 'bottom-2' : 'top-2',
  'border-blue-200 bg-blue-50 text-blue-700'
])
const cellClass = computed(() => [
  'group relative min-h-[112px] border-r border-b border-slate-200 bg-white p-2.5 align-top transition-colors duration-150',
  isHundredPercentCustomer.value ? 'border-l-2 border-l-amber-400 bg-amber-50/30' : '',
  props.customer ? 'cursor-default hover:bg-slate-50' : 'cursor-pointer',
  canDrag.value ? 'cursor-move' : '',
  isDraggingSource.value ? 'opacity-55 ring-1 ring-inset ring-blue-200 bg-slate-50' : '',
  isDropTarget.value ? 'bg-blue-50/60 ring-1 ring-inset ring-blue-300' : '',
  dropPosition.value === 'before' && !isDraggingSource.value ? 'before:absolute before:left-1 before:right-1 before:top-0 before:z-10 before:h-1 before:rounded-full before:bg-blue-500' : '',
  dropPosition.value === 'after' && !isDraggingSource.value ? 'after:absolute after:left-1 after:right-1 after:bottom-0 after:z-10 after:h-1 after:rounded-full after:bg-blue-500' : '',
  dropPosition.value === 'empty' && !isDraggingSource.value ? 'ring-2 ring-inset ring-blue-300 bg-blue-50/70' : ''
])

function activate() {
  if (!props.customer || props.syncing) return
  emit('edit-customer', props.customer)
}

function handleDragStart(event) {
  if (!canDrag.value) {
    event.preventDefault()
    return
  }
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', String(props.customer.id))
  emit('drag-start', {
    customer: props.customer,
    planCustomerId: props.customer.id,
    sourceRegionId: props.customer.regionBucketId
  })
}

function handleDragOver(event) {
  if (props.syncing) return
  const position = dragPosition(event)
  dropPosition.value = position
  event.dataTransfer.dropEffect = 'move'
  emit('drag-over', {
    region: props.region,
    customer: props.customer,
    position,
    rowIndex: props.rowIndex
  })
}

function handleDragLeave() {
  dropPosition.value = null
  emit('drag-leave')
}

function handleDrop(event) {
  if (props.syncing) return
  const position = dragPosition(event)
  dropPosition.value = null
  emit('drop-customer', {
    region: props.region,
    customer: props.customer,
    position,
    rowIndex: props.rowIndex
  })
}

function handleDragEnd() {
  dropPosition.value = null
  emit('drag-end')
}

function dragPosition(event) {
  if (!props.customer) return 'empty'
  const rect = event.currentTarget.getBoundingClientRect()
  return event.clientY - rect.top < rect.height / 2 ? 'before' : 'after'
}
</script>
